import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { uid } from 'uid';
import Snackbar, { SnackbarType } from '../components/Common/Snackbar';

const CLOSE_AUTO = 5000;
const CLOSE_ANIMATION_DURATION = 500;

interface SnackbarItem {
  id: string;
  type: SnackbarType;
  message: string;
  shownAt: Date;
  show: boolean;
  disableAutoClose: boolean;
}

interface SnackbarConfig {
  disableAutoClose?: boolean;
}

interface SnackbarContext {
  show: (
    message: string,
    type?: SnackbarType,
    config?: SnackbarConfig,
  ) => SnackbarItem;
  showDebug: (
    message: string,
    type?: SnackbarType,
    config?: SnackbarConfig,
  ) => SnackbarItem | undefined;
  hide: (id?: string | string[]) => void;
}

export const SnackbarContext = createContext<SnackbarContext>({
  show: () => {
    throw new Error('show() not implemented');
  },
  showDebug: () => {
    throw new Error('showDebug() not implemented');
  },
  hide: () => {},
});

export function SnackbarContextProvider(props: any) {
  const [snackbars, setSnackbars] = useState<SnackbarItem[]>([]);

  const show = useCallback(
    (message: string, type: SnackbarType = 'info', config?: SnackbarConfig) => {
      const id = uid(12);
      const snackbar = {
        id,
        type,
        message,
        shownAt: new Date(),
        show: true,
        disableAutoClose: config?.disableAutoClose || false,
      };
      setSnackbars((prev) => [...prev, snackbar]);

      return snackbar;
    },
    [],
  );

  const showDebug = useCallback(
    (message: string, type: SnackbarType = 'info') => {
      if (import.meta.env.NODE_ENV !== 'development') return;
      return show(message, type);
    },
    [show],
  );

  const hide = useCallback((id?: string | string[]) => {
    setSnackbars((prev) =>
      prev.map((snackbar) => {
        if (Array.isArray(id)) {
          return id.includes(snackbar.id)
            ? { ...snackbar, show: false }
            : snackbar;
        }

        return id === undefined || snackbar.id === id
          ? { ...snackbar, show: false }
          : snackbar;
      }),
    );
  }, []);

  useEffect(() => {
    if (!snackbars.length) return;

    const autoCloseTimer = setInterval(() => {
      const now = new Date();
      const snackbarsToAutoClose = snackbars.filter(
        (snackbar) =>
          snackbar.show &&
          !snackbar.disableAutoClose &&
          snackbar.shownAt.getTime() + CLOSE_AUTO < now.getTime(),
      );

      if (!snackbarsToAutoClose.length) return;

      setSnackbars((prev) =>
        prev.map((snackbar) => {
          if (snackbarsToAutoClose.some((s) => s.id === snackbar.id)) {
            return { ...snackbar, show: false };
          }

          return snackbar;
        }),
      );
    }, 100);

    const removeHiddenSnackbarsTimer = setInterval(() => {
      const snackbarsToClose = snackbars.filter(
        (snackbar) =>
          !snackbar.show &&
          snackbar.shownAt.getTime() + CLOSE_AUTO + CLOSE_ANIMATION_DURATION <
            new Date().getTime(),
      );

      if (!snackbarsToClose.length) return;

      setSnackbars((prev) =>
        prev.filter(
          (snackbar) => !snackbarsToClose.some((s) => s.id === snackbar.id),
        ),
      );
    }, 100);

    return () => {
      clearInterval(autoCloseTimer);
      clearInterval(removeHiddenSnackbarsTimer);
    };
  }, [snackbars]);

  return (
    <SnackbarContext.Provider
      value={{
        show: show,
        showDebug: showDebug,
        hide: hide,
      }}
    >
      <div>{props.children}</div>
      <div
        aria-live="assertive"
        className="pointer-events-none fixed inset-0 z-20 flex items-end px-4 py-6 sm:items-start sm:p-6"
      >
        <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
          {snackbars.map((snackbar) => (
            <Snackbar
              show={snackbar.show}
              key={snackbar.id}
              type={snackbar.type}
              title={snackbar.message}
              onClose={() => {
                hide(snackbar.id);
              }}
            />
          ))}
        </div>
      </div>
    </SnackbarContext.Provider>
  );
}

export const useSnackbarContext = () => {
  return useContext(SnackbarContext);
};
